import React, { useState, useEffect } from "react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import axios from "axios";

const ClientLogos = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    axios
      .get("https://archpoint.onrender.com/api/clients")
      .then((res) => {
        setClients(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Client API Error:", err);
        setLoading(false);
      });
  }, []);

  // Duplicate clients array to create seamless loop
  const duplicatedClients = [...clients, ...clients, ...clients];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const stats = [
    { number: "500+", label: "Projects Delivered" },
    { number: "50+", label: "Happy Clients" },
    { number: "20+", label: "Years Experience" },
  ];

  const splitNumber = (numStr) => {
    const match = numStr.match(/^(\d+)(.*)$/);
    return match ? { value: parseInt(match[1]), suffix: match[2] } : null;
  };

  return (
    <div className="relative py-20 bg-white overflow-hidden">
      {/* Custom CSS for marquee animation */}
      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }

        .marquee {
          animation: marquee 20s linear infinite;
        }

        .marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Subtle Background Pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #fbbf24 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      ></div>

      {/* Geometric Accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-amber-50 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-yellow-50 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div
          className={`text-center mb-16 transition-all duration-800 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="mx-4 text-sm font-semibold text-amber-600 uppercase tracking-wider">
              Our Partners
            </span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Trusted by Industry
            <span className="block text-amber-600">Leaders</span>
          </h2>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We partner with forward-thinking organizations to deliver
            exceptional results and drive meaningful business growth.
          </p>
        </div>

        {/* Marquee Carousel Container */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading our partners...</p>
          </div>
        ) : clients.length > 0 ? (
          <div
            className={`relative mb-16 transition-all duration-800 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {/* Gradient Overlays for Seamless Effect */}
            <div className="absolute left-0 top-0 w-20 h-full bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 w-20 h-full bg-gradient-to-l from-white to-transparent z-10"></div>

            {/* Marquee Container */}
            <div className="overflow-hidden">
              <div
                className="marquee flex items-center gap-8"
                style={{ width: "calc(100% * 3)" }}
              >
                {duplicatedClients.map((client, index) => (
                  <div
                    key={`${client._id || client.name}-${index}`}
                    className="group relative flex-shrink-0 w-80"
                  >
                    {/* Card Container */}
                    <div className="relative bg-white rounded-xl border border-gray-100 p-8 h-32 flex items-center justify-center transition-all duration-300 hover:border-amber-200 hover:shadow-lg hover:-translate-y-1">
                      {/* Hover Accent */}
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {/* Logo */}
                      <div className="relative z-10 flex items-center justify-center w-full h-full">
                        <img
                          src={client.logoUrl}
                          alt={client.name}
                          className="max-h-16 max-w-full object-contain transition-all duration-300 group-hover:scale-105 filter grayscale group-hover:grayscale-0"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        {/* Fallback */}
                        <div className="hidden items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg text-white font-bold text-xl shadow-lg">
                          {client.name.charAt(0)}
                        </div>
                      </div>

                      {/* Subtle Border Animation */}
                      <div className="absolute inset-0 rounded-xl border-2 border-amber-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                    </div>

                    {/* Client Name */}
                    <div className="text-center mt-4">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-amber-700 transition-colors duration-300">
                        {client.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-600">No clients found.</p>
          </div>
        )}

        {/* Stats Section */}
        <div
          ref={ref}
          className={`grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 transition-all duration-800 delay-500 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {stats.map((stat, index) => {
            const numberParts = splitNumber(stat.number);
            return (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-amber-600 mb-2">
                  {numberParts ? (
                    <>
                      {inView ? (
                        <CountUp end={numberParts.value} duration={2} />
                      ) : (
                        "0"
                      )}
                      {numberParts.suffix}
                    </>
                  ) : (
                    stat.number
                  )}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div
          className={`text-center transition-all duration-800 delay-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-gray-600 mb-6">
            Ready to join our growing family of successful partners?
          </p>
          <Link
            to="/about#contact"
            onClick={() => {
              setTimeout(() => {
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" });
              }, 100);
            }}
            className="inline-flex items-center px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-amber-200 group"
          >
            <span>Start Your Project</span>
            <svg
              className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientLogos;